<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="text-center space-y-2">
      <h2 class="text-2xl font-bold text-foreground">
        {{ $t('vet.signup.petType.title') }}
      </h2>
      <p class="text-muted-foreground">
        {{ $t('vet.signup.petType.description') }}
      </p>
    </div>

    <!-- Species Selection Grid -->
    <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
      <button
        v-for="species in speciesOptions"
        :key="species.value"
        type="button"
        :class="[
          'flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-200 hover:border-bubba-blue/50',
          selectedSpecies === species.value
            ? 'border-bubba-blue bg-bubba-blue/5 shadow-md'
            : 'border-border hover:bg-accent/50'
        ]"
        @click="selectSpecies(species.value)"
      >
        <Icon :name="species.icon" size="32" :class="selectedSpecies === species.value ? 'text-bubba-blue' : 'text-muted-foreground'" />
        <span :class="['mt-2 text-sm font-medium', selectedSpecies === species.value ? 'text-bubba-blue' : 'text-foreground']">
          {{ $t(`vet.signup.petType.species.${species.value}`) }}
        </span>
      </button>
    </div>

    <!-- Breed Selection (for dogs) -->
    <Transition name="fade">
      <div v-if="selectedSpecies === 'dog'" class="space-y-2">
        <UiLabel for="breed">{{ $t('vet.signup.petType.breed') }}</UiLabel>
        <div class="relative">
          <UiInput
            id="breed"
            v-model="breedSearch"
            type="text"
            :placeholder="$ts('vet.signup.petType.breedPlaceholder')"
            autocomplete="off"
            @focus="showBreedDropdown = true"
            @blur="handleBreedBlur"
          />

          <!-- Breed Dropdown -->
          <Transition name="dropdown">
            <div
              v-if="showBreedDropdown && filteredBreeds.length > 0"
              class="absolute z-10 w-full mt-1 max-h-60 overflow-auto rounded-md border bg-popover shadow-lg"
            >
              <button
                v-for="breed in filteredBreeds"
                :key="breed.slug"
                type="button"
                class="w-full px-3 py-2 text-left text-sm hover:bg-accent transition-colors"
                @mousedown.prevent="selectBreed(breed)"
              >
                {{ breed.name }}
              </button>
            </div>
          </Transition>
        </div>
        <p class="text-xs text-muted-foreground">
          {{ $t('vet.signup.petType.breedOptional') }}
        </p>
      </div>
    </Transition>

    <!-- Navigation -->
    <div class="flex justify-end pt-4">
      <UiButton
        :disabled="!isValid"
        class="bg-bubba-blue hover:bg-bubba-blue/90"
        @click="handleNext"
      >
        {{ $t('vet.signup.navigation.next') }}
        <Icon name="tabler:arrow-right" size="16" />
      </UiButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { api } from '@convex/_generated/api';
import { useI18n, useNuxtApp } from '#imports';
import { createLogger } from '#bubba/shared/utils/logger';
import type { SignupFormData } from '#composables/vet/useVetSignupWizard';

const log = createLogger('StepPetType', { enabled: true });

const props = defineProps<{
  formData: SignupFormData;
}>();

const emit = defineEmits<{
  update: [data: Partial<SignupFormData>];
  next: [];
}>();

const { $ts, $getLocale } = useI18n();

const speciesOptions = [
  { value: 'dog', icon: 'tabler:dog' },
  { value: 'cat', icon: 'tabler:cat' },
  { value: 'bird', icon: 'tabler:feather' },
  { value: 'rabbit', icon: 'tabler:paw' },
  { value: 'hamster', icon: 'tabler:paw' },
  { value: 'guinea_pig', icon: 'tabler:paw' },
  { value: 'fish', icon: 'tabler:fish' },
  { value: 'turtle', icon: 'tabler:paw' },
  { value: 'snake', icon: 'tabler:paw' },
  { value: 'lizard', icon: 'tabler:paw' },
  { value: 'ferret', icon: 'tabler:paw' },
  { value: 'horse', icon: 'tabler:horse-toy' },
  { value: 'other', icon: 'tabler:paw' },
] as const;

const selectedSpecies = ref(props.formData.species || '');
const breedSearch = ref(props.formData.breedName || '');
const selectedBreedSlug = ref(props.formData.breedSlug || '');
const showBreedDropdown = ref(false);

// Client-side only breeds fetching
const breeds = ref<Array<{ slug: string; name: string }>>([]);
const breedsLoading = ref(false);

onMounted(async () => {
  // Fetch breeds on client only
  try {
    breedsLoading.value = true;
    const { $convex } = useNuxtApp();
    if ($convex) {
      const result = await ($convex as any).query(api.data.dogBreeds.listDogBreeds, {
        locale: $getLocale(),
        limit: 500,
      });
      breeds.value = result;
    }
  } catch (error) {
    log.error('Failed to fetch breeds:', error);
  } finally {
    breedsLoading.value = false;
  }
});

const filteredBreeds = computed(() => {
  if (!breeds.value.length) return [];
  if (!breedSearch.value.trim()) return breeds.value.slice(0, 20);

  const search = breedSearch.value.toLowerCase().trim();
  return breeds.value
    .filter(b => b.name.toLowerCase().includes(search))
    .slice(0, 20);
});

const isValid = computed(() => {
  return selectedSpecies.value !== '';
});

function selectSpecies(species: string) {
  selectedSpecies.value = species;
  emit('update', { species });

  // Clear breed if switching away from dog
  if (species !== 'dog') {
    breedSearch.value = '';
    selectedBreedSlug.value = '';
    emit('update', { breedSlug: undefined, breedName: undefined });
  }
}

function selectBreed(breed: { slug: string; name: string }) {
  breedSearch.value = breed.name;
  selectedBreedSlug.value = breed.slug;
  showBreedDropdown.value = false;
  emit('update', { breedSlug: breed.slug, breedName: breed.name });
}

function handleBreedBlur() {
  // Delay to allow click on dropdown item
  setTimeout(() => {
    showBreedDropdown.value = false;
  }, 150);
}

function handleNext() {
  if (isValid.value) {
    emit('next');
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
